import { Button } from 'blaze-ui/Button'
import Styles from './EditPostMenu.module.scss'
import { Brand, Caption, Design, FileEdit2, Microphone, Plus } from 'blaze-ui/icons'
import { useModals } from 'almanac-ui/Modal'
import { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'
import { useQueryClient } from 'react-query'
import { maxFiles } from 'components/ArticlePostPreview/ArticlePostPreviewContext'
import { useLocation, useHistory, generatePath, Link } from 'react-router-dom'
import { BRAND_KITS_PATH, DESIGN_PATH, DOC_PATH } from 'paths'
import { ImageMenuModal } from 'components/ImageMenu/ImageMenuModal/ImageMenuModal'
import { ImageMenuSelectedImage } from 'components/ImageMenu/ImageMenuModal/ImageMenuContext'
import { MediaObject } from 'types'
import { useCreateMediaObjectsWithoutPresigning } from 'hooks/queries/mediaObjects/useCreateMediaObjects'
import { useRef } from 'react'
import { useInsertMediaObjects } from 'hooks/queries/designs/useInsertMediaObjects'
import { ToolbarMenu } from '../Toolbar/ToolbarMenu'
import { ButtonLink } from 'blaze-ui/Button/ButtonLink'
import { EditPostMenuState } from '../Toolbar/Toolbar'
import { EditCaptionButton } from './EditCaptionButton'
import { AssetType } from 'hooks/useAlmanacFileSubscription'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { AudioEditorModal } from 'components/Previews/components/VideoPlayer/AudioEditorModal'
import { calendarKeys } from 'hooks/queries/calendar/calendarKeys'
import { removeFileDetailCache } from 'hooks/queries/files/cacheUtils'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { DesignChannel } from 'components/NewFileModal/Types'

export const EditPostMenu = ({
  scheduledPost,
  almanacFile,
  isBlankDesign,
  onLoadingChange,
  animationState = 'idle',
  targetState,
  onClose,
}: {
  scheduledPost: ScheduledPostShow
  almanacFile: AlmanacFileShowItem
  isBlankDesign: boolean
  onLoadingChange?: (isLoading: boolean) => void
  animationState?: 'idle' | 'animating'
  targetState?: EditPostMenuState | null
  onClose?: () => void
}) => {
  const { openModal, closeModal } = useModals()
  const queryClient = useQueryClient()
  const location = useLocation()
  const history = useHistory()
  const { mutateAsync: createMediaObjectsWithoutPresigning } = useCreateMediaObjectsWithoutPresigning()
  const metadataRef = useRef<any[]>([])
  const approvalsEnabled = useFlipperFlag('post_approvals')
  const audioEditorEnabled = useFlipperFlag('audio_editor_modal')
  const workspaceId = useCurrentWorkspaceId()
  const isSystemRedesignEnabled = useIsSystemRedesign()

  const { mutate: insertMediaObjects } = useInsertMediaObjects(
    {
      designId: almanacFile.key,
    },
    {
      onMutate: () => {
        onLoadingChange?.(true)
      },
      onSuccess: (data) => {
        queryClient.invalidateQueries(['calendarItems'])
        queryClient.invalidateQueries(['calendar'])
        if (almanacFile?.scheduled_post) {
          queryClient.invalidateQueries(['scheduledPosts', almanacFile.scheduled_post.id])
        }
      },
      onSettled: () => {
        onLoadingChange?.(false)
      },
    },
  )

  const handleMediaSelection = async (images: ImageMenuSelectedImage[]) => {
    const integration = scheduledPost.external_integration
    const maxFilesCount = integration ? maxFiles[integration.provider] : Infinity
    let newFiles: MediaObject[] = []

    // Process images sequentially with Promise.all to wait for all async operations
    await Promise.all(
      images.map(async (image, index) => {
        const { id, ...file } = image
        const mediaObjects = await createMediaObjectsWithoutPresigning([
          {
            ...file,
            ...metadataRef.current[index],
            url: file.mime_type?.startsWith('video') && file.source_url ? file.source_url : file.url,
          },
        ])
        newFiles.push(mediaObjects[0])
      }),
    )

    // Update the scheduled post with the new media object IDs
    const currentMediaObjectIds = scheduledPost?.media_objects?.map((obj) => obj.id) || []
    const newMediaObjectIds = newFiles.map((file) => file.id)
    const allMediaObjectIds = [...currentMediaObjectIds, ...newMediaObjectIds].slice(0, maxFilesCount)

    insertMediaObjects({
      media_object_ids: allMediaObjectIds,
    })
  }

  // TODO: Wire up image uploading
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleAddMediaClick = () => {
    const integration = scheduledPost.external_integration
    const maxCount = integration
      ? maxFiles[integration.provider] - (scheduledPost?.media_objects?.length || 0)
      : Infinity

    openModal(
      ImageMenuModal,
      {
        maxCount,
        hiddenProviders: ['gifs'],
        uploadMedia: true,
        onUse: handleMediaSelection,
      },
      {
        newStack: true,
      },
    )
  }

  const handleOpenBrandKitStyles = () => {
    history.push({ pathname: generatePath(BRAND_KITS_PATH, { workspaceId }), hash: 'styles_voice' })
  }

  const to = generatePath(DESIGN_PATH, { key: almanacFile.key })

  const handleVisualClick = () => {
    const isOnDesignPath = location.pathname === to

    if (isOnDesignPath) {
      closeModal()
    } else {
      onClose?.()
      closeModal()
      history.push(to)
    }
  }

  // Determine current and target button states
  const getCurrentButtonState = () => {
    if (almanacFile.asset_type === AssetType.Article) {
      return { type: 'document' as const, isBlankDesign: false }
    } else if (isBlankDesign) {
      return { type: 'blank' as const, isBlankDesign }
    } else {
      return { type: 'default' as const, isBlankDesign }
    }
  }

  const currentButtonState = getCurrentButtonState()

  // During animation, use target state; otherwise use current state
  const activeButtonState = animationState === 'animating' && targetState ? targetState : currentButtonState

  const buttonVariant = isSystemRedesignEnabled ? 'secondary' : 'tertiary'

  const shouldHideCaptionField = scheduledPost?.source_channel === DesignChannel.InstagramStory

  // Render buttons based on active state
  const renderButtons = (state: EditPostMenuState) => {
    if (state.type === 'document') {
      if (approvalsEnabled) {
        return (
          <Button
            variant={buttonVariant}
            size="md"
            frontIcon={FileEdit2}
            fullWidth
            onPress={() => {
              history.push(generatePath(DOC_PATH, { slug: almanacFile.key }))
              setTimeout(() => {
                onClose?.()
              }, 100)
            }}
          >
            Edit Document
          </Button>
        )
      } else {
        return (
          <ButtonLink
            justifyContent="flex-start"
            variant={buttonVariant}
            size="md"
            frontIcon={FileEdit2}
            fullWidth
            vertical
            elementType={Link}
            to={generatePath(DOC_PATH, { slug: almanacFile.key })}
            onPress={() => {
              setTimeout(() => {
                onClose?.()
              }, 100)
            }}
          >
            Edit Document
          </ButtonLink>
        )
      }
    } else if (state.type === 'blank') {
      return (
        <>
          <CaptionButtonWithTooltip shouldHideCaptionField={shouldHideCaptionField}>
            <EditCaptionButton
              scheduledPost={scheduledPost}
              almanacFileKey={almanacFile.key}
              variant={buttonVariant}
              size="md"
              frontIcon={FileEdit2}
              justifyContent="flex-start"
              fullWidth
              vertical
              shouldHideCaptionField={shouldHideCaptionField}
            />
          </CaptionButtonWithTooltip>
          <Button
            justifyContent="flex-start"
            variant={isSystemRedesignEnabled ? 'secondary' : 'ghost-tertiary'}
            size="md"
            frontIcon={Plus}
            onPress={handleAddMediaClick}
            fullWidth
            vertical
          >
            Add Media
          </Button>
        </>
      )
    } else {
      if (approvalsEnabled) {
        return (
          <>
            {location.pathname === to ? (
              <Button variant={buttonVariant} size="md" frontIcon={Design} fullWidth onPress={handleVisualClick}>
                Design
              </Button>
            ) : (
              <ButtonLink
                variant={buttonVariant}
                size="md"
                frontIcon={Design}
                elementType={Link}
                to={to}
                onPress={() => {
                  setTimeout(() => {
                    onClose?.()
                  }, 100)
                }}
                fullWidth
              >
                Design
              </ButtonLink>
            )}

            <CaptionButtonWithTooltip shouldHideCaptionField={shouldHideCaptionField}>
              <EditCaptionButton
                scheduledPost={scheduledPost}
                almanacFileKey={almanacFile.key}
                variant={buttonVariant}
                size="md"
                frontIcon={Caption}
                justifyContent="center"
                vertical={false}
                fullWidth
                shouldHideCaptionField={shouldHideCaptionField}
              />
            </CaptionButtonWithTooltip>
          </>
        )
      } else {
        return (
          <>
            <CaptionButtonWithTooltip shouldHideCaptionField={shouldHideCaptionField}>
              <EditCaptionButton
                scheduledPost={scheduledPost}
                almanacFileKey={almanacFile.key}
                variant={buttonVariant}
                size="md"
                frontIcon={FileEdit2}
                justifyContent="flex-start"
                fullWidth
                vertical
              />
            </CaptionButtonWithTooltip>
            {location.pathname === to ? (
              <Button
                justifyContent="flex-start"
                variant={buttonVariant}
                size="md"
                frontIcon={Design}
                onPress={handleVisualClick}
                fullWidth
                vertical
              >
                Edit Design
              </Button>
            ) : (
              <ButtonLink
                justifyContent="flex-start"
                variant={buttonVariant}
                size="md"
                frontIcon={Design}
                elementType={Link}
                to={to}
                onPress={() => {
                  setTimeout(() => {
                    onClose?.()
                  }, 100)
                }}
                fullWidth
                vertical
              >
                Edit Design
              </ButtonLink>
            )}
          </>
        )
      }
    }
  }

  const handleAudioTransformComplete = () => {
    removeFileDetailCache(queryClient, workspaceId, almanacFile.key)
    queryClient.invalidateQueries({ queryKey: calendarKeys.detail(almanacFile.key) })
  }

  const videoMediaObject = scheduledPost?.media_objects?.find((object) => object.mime_type?.startsWith('video/'))
  const handleOpenAudioEditor = () => {
    if (!videoMediaObject) return
    openModal(AudioEditorModal, {
      mediaObject: videoMediaObject,
      almanacFileKey: almanacFile.key,
      onAudioTransformComplete: handleAudioTransformComplete,
    })
  }

  return (
    <ToolbarMenu
      title={approvalsEnabled ? 'Improve' : 'Make Changes'}
      height={isSystemRedesignEnabled ? 20 : undefined}
    >
      <div className={Styles.buttons} data-animation-state={animationState}>
        {renderButtons(activeButtonState)}
      </div>
      {approvalsEnabled && activeButtonState.type === 'default' && (
        <div className={Styles.buttons} data-animation-state={animationState}>
          {audioEditorEnabled && (
            <Button
              variant={buttonVariant}
              size="md"
              frontIcon={Microphone}
              fullWidth
              isDisabled={!videoMediaObject}
              onPress={handleOpenAudioEditor}
            >
              Audio
            </Button>
          )}
          <Button variant={buttonVariant} size="md" frontIcon={Brand} fullWidth onPress={handleOpenBrandKitStyles}>
            Brand
          </Button>
        </div>
      )}
    </ToolbarMenu>
  )
}

const CaptionButtonWithTooltip = ({ children, shouldHideCaptionField }: { children: React.ReactElement, shouldHideCaptionField: boolean }) => {
  return (
    <Tooltip className={Styles.tooltipWrapper} label="Instagram Stories do not show captions below the post" hide={!shouldHideCaptionField}>
      <div className={Styles.tooltipWrapper}>
        {children}
      </div>
    </Tooltip>
  )
}